/* global Audio */
import RealFluentdLogger from 'app/services/fluentdLogger';
import bindMethods from 'app/utils/bindMethods';

export const LOADING_ERROR_LISTENER = 'LOADING_ERROR_LISTENER';
export const PLAYING_ERROR_LISTENER = 'PLAYING_ERROR_LISTENER';

export class AudioPlayer {
  constructor() {
    this.errorListeners = {};

    this.bound = bindMethods(this, ['finishedLoadingListener']);

    this.audio = new Audio();
    this.audio.addEventListener('loadeddata', this.bound.finishedLoadingListener, false);
  }

  finishedLoadingListener() {
    this.isLoading = false;
    this.abortedLoading = false;
    if (this.pauseAfterLoad) {
      this.audio.pause();
      this.pauseAfterLoad = false;
    }
  }

  addLoadedDataListener(listener) {
    // Make sure only one listener is attached
    this.removeLoadedDataListener();
    this.audio.addEventListener('loadeddata', listener, false);
    this.loadedDataListener = listener;
  }

  addEndedListener(listener) {
    // Make sure only one listener is attached
    this.removeEndedListener();
    this.audio.addEventListener('ended', listener, false);
    this.endedListener = listener;
  }

  addErrorListener(listener, type) {
    if (type === LOADING_ERROR_LISTENER || type === PLAYING_ERROR_LISTENER) {
      this.removeErrorListener(type);
      this.audio.addEventListener('error', listener, false);
      this.errorListeners[type] = listener;
    }
  }

  removeLoadedDataListener() {
    if (this.loadedDataListener) {
      this.audio.removeEventListener('loadeddata', this.loadedDataListener, false);
      this.loadedDataListener = undefined;
    }
  }

  removeEndedListener() {
    if (this.endedListener) {
      this.audio.removeEventListener('ended', this.endedListener, false);
      this.endedListener = undefined;
    }
  }

  removeErrorListener(type) {
    if (this.errorListeners[type]) {
      this.audio.removeEventListener('error', this.errorListeners[type], false);
      delete this.errorListeners[type];
    }
  }

  removeAllListeners() {
    this.removeLoadedDataListener();
    this.removeEndedListener();
    this.removeErrorListener(LOADING_ERROR_LISTENER);
    this.removeErrorListener(PLAYING_ERROR_LISTENER);
  }

  play() {
    this.audio.play();
  }

  pause() {
    if (this.isLoading) {
      // Trying to pause while loading will cause an error, therefore defer the call
      this.pauseAfterLoad = true;
    } else {
      this.audio.pause();
    }
  }

  load(url) {
    if (this.isLoading) {
      try {
        // This will stop the network download, see
        // https://developer.mozilla.org/de/docs/Web/HTML/Using_HTML5_audio_and_video
        this.abortedLoading = true;
        this.audio.src = '';
      } catch (error) {
        // If the audio is currently loading, this error is expected
      }
    } else {
      this.abortedLoading = false;
    }

    this.pauseAfterLoad = false;
    this.isLoading = true;
    this.audio.src = url;
    // Calling play is needed to actually trigger loading the sound on Chrome mobile
    // https://bugs.chromium.org/p/chromium/issues/detail?id=138132
    const playPromise = this.audio.play();
    // If the play method returns a promise, we need to catch errors when aborting the load
    if (playPromise && playPromise instanceof Promise) {
      playPromise.catch(error => {
        if (this.isLoading && this.abortedLoading) {
          // If the audio was aborted during the loading phase, this error is expected
          this.abortedLoading = false;
        } else {
          throw error;
        }
      });
    }
  }
}

let globalAudioPlayer;

let notifyPrevious = () => {};

const getGlobalAudioPlayer = () => {
  if (!globalAudioPlayer) {
    globalAudioPlayer = new AudioPlayer();
  }

  return globalAudioPlayer;
};

export function play(url, deps = {}) {
  const {
    FluentdLogger = RealFluentdLogger,
    audioPlayer = getGlobalAudioPlayer(),
  } = deps;

  notifyPrevious();

  audioPlayer.load(url);

  return {
    loading: new Promise((resolve, reject) => {
      audioPlayer.addLoadedDataListener(loaded);
      audioPlayer.addErrorListener(rejected, LOADING_ERROR_LISTENER);

      function loaded() {
        audioPlayer.removeLoadedDataListener();
        audioPlayer.removeErrorListener(LOADING_ERROR_LISTENER);
        resolve();
      }

      function rejected(e) {
        audioPlayer.removeLoadedDataListener();
        audioPlayer.removeErrorListener(LOADING_ERROR_LISTENER);
        reject();

        FluentdLogger.info({ name: 'Audio.LoadingError', message: e.message });
      }
    }),
    playing: new Promise((resolve, reject) => {
      notifyPrevious = played;

      audioPlayer.addEndedListener(played);
      audioPlayer.addErrorListener(rejected, PLAYING_ERROR_LISTENER);

      function played() {
        notifyPrevious = () => {};
        audioPlayer.removeAllListeners();
        resolve();
      }

      function rejected(e) {
        audioPlayer.removeAllListeners();
        reject();

        FluentdLogger.info({ name: 'Audio.PlayingError', message: e.message });
      }
    }),
  };
}

export function pause(deps = {}) {
  const {
    audioPlayer = getGlobalAudioPlayer(),
  } = deps;

  audioPlayer.pause();
  notifyPrevious();
}

export function reset() {
  if (globalAudioPlayer) {
    globalAudioPlayer.removeAllListeners();
    globalAudioPlayer = undefined;
  }
  notifyPrevious = () => {};
}
